import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { Save, Loader2, Info } from 'lucide-react';
import type { MerchControlData } from './chestConfigTypes';

const MERCH_SUBTYPES = [
    { key: 'merch_camiseta_corta', label: 'Camiseta manga corta' },
    { key: 'merch_gorra', label: 'Gorra' },
    { key: 'merch_camiseta_larga', label: 'Camiseta manga larga' },
    { key: 'merch_sudadera', label: 'Sudadera' },
] as const;

interface MerchTabProps {
    saved: MerchControlData;
    onSaved: () => void;
}

export function MerchTab({ saved, onSaved }: MerchTabProps) {
    const queryClient = useQueryClient();
    const [budgets, setBudgets] = useState<Record<string, number>>({});
    const [granted, setGranted] = useState<Record<string, number>>({});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        setBudgets(Object.fromEntries(saved.quotas.map((q) => [q.subtype, q.budget_units])));
        setGranted(Object.fromEntries(saved.quotas.map((q) => [q.subtype, q.granted_units])));
        setHasChanges(false);
    }, [saved]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            const quotas = MERCH_SUBTYPES.map(({ key }) => ({
                subtype: key,
                budget_units: budgets[key] ?? 0,
                granted_units: granted[key] ?? 0,
            }));
            const { error } = await dbClient.rpc('admin_upsert_merch_reward_control', {
                p_weights: saved.weights,
                p_quotas: quotas,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'chest-config'] });
            setHasChanges(false);
            onSaved();
        },
    });

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-4 text-sm text-muted-foreground flex gap-2">
                <Info size={16} className="text-fuchsia-400 shrink-0 mt-0.5" />
                <div className="space-y-2">
                    <p>
                        El merchandising <strong className="text-foreground">ya no sale en los cofres</strong>. Se
                        desbloquea en hitos de misión. El cupo de abajo es el de{' '}
                        <strong className="text-foreground">suscripciones Pro</strong> que pueden salir de un cofre en
                        toda la app.
                    </p>
                    <p>
                        Semana: <span className="font-mono text-foreground">{saved.period_week || '—'}</span>. Súper
                        tesoros Pro:{' '}
                        <span className="font-mono text-foreground">
                            {saved.super_treasure_weekly?.granted_units ?? 0}/
                            {saved.super_treasure_weekly?.budget_units ?? 10}
                        </span>
                    </p>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={() => saveMutation.mutate()}
                    disabled={!hasChanges || saveMutation.isPending}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium',
                        hasChanges ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                >
                    {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar cupos
                </button>
            </div>

            <div className="rounded-xl border border-border p-4 space-y-3">
                <h3 className="text-sm font-semibold">Cupos semanales (unidades)</h3>
                {MERCH_SUBTYPES.map(({ key, label }) => {
                    const used = granted[key] ?? 0;
                    const budget = budgets[key] ?? 0;
                    const pct = budget > 0 ? Math.min(100, (used / budget) * 100) : 0;
                    return (
                        <div key={key} className="space-y-1">
                            <div className="flex items-center gap-3">
                                <div className="flex-1 text-sm">{label}</div>
                                <span className="text-xs font-mono text-muted-foreground w-16 text-right">
                                    {used}/{budget}
                                </span>
                                <input
                                    type="number"
                                    min={used}
                                    step={1}
                                    value={budget}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                        if (isNaN(val) || val < 0) return;
                                        setBudgets((prev) => ({ ...prev, [key]: val }));
                                        setHasChanges(true);
                                    }}
                                    className="w-20 px-2 py-1.5 rounded-lg border border-border bg-background text-sm font-mono text-right"
                                />
                            </div>
                            <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                                <div
                                    className={cn('h-full rounded-full', pct >= 90 ? 'bg-red-500' : 'bg-fuchsia-500')}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {saveMutation.isError && (
                <div className="px-4 py-3 rounded-xl border bg-red-500/5 border-red-500/20 text-sm text-red-400">
                    Error: {(saveMutation.error as Error)?.message}
                </div>
            )}
        </div>
    );
}
